namespace $.$$ {
	type $shm_hitalama_dragtree_node = {
		id: any
		parent: any
		depth: number
	}

	type $shm_hitalama_dragtree_layout = {
		nodes: readonly $shm_hitalama_dragtree_node[]
		tops: readonly number[]
		index_by_id: ReadonlyMap< any, number >
		parent_by_id: ReadonlyMap< any, any >
		depth_by_id: ReadonlyMap< any, number >
		height: number
	}

	const $shm_hitalama_dragtree_drop_activate_threshold = 4

	export class $shm_hitalama_dragtree extends $.$shm_hitalama_dragtree {

		_drag_id: any = null
		_drag_start_x = 0
		_drag_start_y = 0
		_drop_ready = false
		_layout_cache = new WeakMap< readonly $shm_hitalama_dragtree_node[], $shm_hitalama_dragtree_layout >

		id( id: any ): string {
			return id
		}

		@ $mol_mem
		items() {
			return this.visible_nodes().map( node => this.Drag( node.id ) )
		}

		toggle_display( id: any ) {
			return this.has_children( id ) ? 'flex' : 'none'
		}

		@ $mol_mem_key
		toggle_expanded( id: any, next?: boolean ) {
			const children = this.children( id )
			if( next !== undefined && children.length > 0 ) this.expanded( id, next )
			return this.expanded( id ) || this.has_preview_child( id )
		}

		has_children( id: any ) {
			return this.children( id ).length > 0 || this.has_preview_child( id )
		}

		has_preview_child( id: any ) {
			return this.drop_active() && this.drop_parent() === id
		}

		guide_views( id: any ) {
			if( this.dragged( id ) && this.drop_active() ) return []
			const index = this.visible_index( id )
			if( index < 0 ) return []
			return this.guides_for_preview_node( index, 'row' )
		}

		drop_guide_views() {
			if( !this.drop_active() ) return []
			const index = this.visible_nodes().findIndex( node => node.id === this._drag_id )
			if( index < 0 ) return []
			return this.guides_for_preview_node( index, 'drop' )
		}

		guides_for_preview_node( index: number, prefix: string ) {
			const nodes = this.visible_nodes()
			const node = nodes[ index ]
			if( !node ) return []

			const depth = node.depth
			const guides: $mol_view[] = []

			for( let level = 0; level < depth; ++level ) {
				const branch = this.ancestor_at_depth_in_nodes( index, level + 1, nodes )
				if( branch == null ) continue

				const last = !this.has_next_sibling_in_nodes( branch, nodes )
				if( level < depth - 1 && last ) continue

				guides.push( this.Guide( `${ prefix }|${ index }|${ depth }|${ level }|vertical|${ last ? 1 : 0 }` ) )
			}

			if( depth > 0 ) guides.push( this.Guide( `${ prefix }|${ index }|${ depth }|${ depth - 1 }|horizontal|0` ) )
			return guides
		}

		guide_parts( key: string ) {
			const parts = key.split( '|' )
			if( parts.length >= 6 ) {
				return {
					depth: Number( parts[2] ),
					level: Number( parts[3] ),
					orientation: parts[4],
					last: parts[5] === '1',
				}
			}

			const [ , depth, level ] = key.split( '/' ).map( Number )
			return { depth, level, orientation: 'vertical', last: false }
		}

		guide_left_px( key: string ) {
			const { depth, level } = this.guide_parts( key )
			return ( ( level + .75 - depth ) * this.indent() ) + 'px'
		}

		guide_top_px( key: string ) {
			const { orientation } = this.guide_parts( key )
			return orientation === 'horizontal' ? '50%' : '0px'
		}

		guide_bottom_px( key: string ) {
			const { orientation, last } = this.guide_parts( key )
			if( orientation === 'horizontal' ) return 'auto'
			return last ? '50%' : '0px'
		}

		guide_width_px( key: string ) {
			const { orientation } = this.guide_parts( key )
			return orientation === 'horizontal' ? Math.ceil( this.indent() * .75 ) + 'px' : '1px'
		}

		guide_height_px( key: string ) {
			const { orientation } = this.guide_parts( key )
			return orientation === 'horizontal' ? '1px' : 'auto'
		}

		guide_orientation( key: string ) {
			return this.guide_parts( key ).orientation
		}

		drop_depth() {
			return Math.round( this.drop_left() / this.indent() )
		}

		@ $mol_action
		toggle_pointerdown( id: any, next?: PointerEvent ) {
			if( next ) next.stopPropagation()
			return next ?? null
		}

		on_drag_start( id: any, next: PointerEvent ) {
			this._drag_id = id
			this._drop_ready = false
			this._drag_start_x = this.relative_x( id )
			this._drag_start_y = this.relative_y( id )
			this.drag_x( this._drag_start_x )
			this.drag_y( this._drag_start_y )
		}

		on_drag_end( id: any, next?: PointerEvent ) {
			this.apply_drop( id )
			this.drop_active( false )
			this._drag_id = null
			this._drop_ready = false
			this.drag_x( 0 )
			this.drag_y( 0 )
		}

		@ $mol_action
		drop_activate( id: any ) {
			if( this._drop_ready ) return
			if( this._drag_id !== id ) return
			if( !this.dragged( id ) ) return

			this._drop_ready = true
			if( this.children( id ).length ) this.expanded( id, false )
		}

		drop_moved() {
			const dx = this.drag_x() - this._drag_start_x
			const dy = this.drag_y() - this._drag_start_y
			return dx * dx + dy * dy > $shm_hitalama_dragtree_drop_activate_threshold ** 2
		}

		@ $mol_mem_key
		x( id: any, next?: number ): number {
			if( !this.dragged( id ) ) return this.relative_x( id )
			if( next === undefined ) return this.drag_x()
			this.drag_x( Math.max( 0, next ) )
			if( this.drop_moved() ) this.drop_activate( id )
			return next
		}

		@ $mol_mem_key
		y( id: any, next?: number ): number {
			if( !this.dragged( id ) ) return this.relative_y( id )
			if( next === undefined ) return this.drag_y()
			this.drag_y( Math.max( 0, next ) )
			if( this.drop_moved() ) this.drop_activate( id )
			if( this._drop_ready ) this.set_drop( id, this.drag_x(), this.drag_y() )
			return next
		}

		@ $mol_action
		set_drop( id: any, x: number, y: number ) {
			const target = this.drop_target( id, x, y )
			this.drop_parent( target.parent )
			this.drop_index( target.index )
			this.drop_left( target.depth * this.indent() )
			this.drop_top( target.top )
			this.drop_height( this.item_height( id ) )
			this.drop_active( true )
		}

		@ $mol_action
		apply_drop( id: any ) {
			if( !this.drop_active() ) return

			const old_parent = this.parent_of( id )
			const next_parent = this.drop_parent()
			const old_siblings = this.siblings( old_parent ).filter( child => child !== id )
			this.siblings( old_parent, old_siblings )

			const next_siblings = [
				... this.siblings( next_parent )
			].filter( child => child !== id )
			next_siblings.splice( this.drop_index(), 0, id )
			this.siblings( next_parent, next_siblings )
		}

		siblings( parent: any, next?: any[] ) {
			return parent == null ? this.roots( next ) : this.children( parent, next )
		}

		drop_target( id: any, x: number, y: number ) {
			const base = this.base_nodes( id )
			const layout = this.layout( base )
			const insert = this.nearest_insert( layout, y )
			const max_depth = this.max_depth( layout.nodes, insert )
			const desired_depth = Math.max( 0, Math.min( max_depth, Math.round( x / this.indent() ) ) )
			const depth = this.allowed_depth( layout.nodes, insert, desired_depth )
			const parent = this.parent_by_depth( layout.nodes, insert, depth )
			const sibling_index = this.sibling_index( layout.nodes, insert, parent )
			const target_index = this.absolute_index( layout.nodes, parent, sibling_index )
			const top = this.top_by_index( layout, target_index )
			return { parent, index: sibling_index, depth, top }
		}

		nearest_insert( layout: $shm_hitalama_dragtree_layout, y: number ) {
			const height = this.item_height_default()
			return Math.max( 0, Math.min( layout.nodes.length, Math.round( y / height ) ) )
		}

		max_depth( nodes: readonly $shm_hitalama_dragtree_node[], insert: number ) {
			if( insert === 0 ) return 0
			return nodes[ insert - 1 ].depth + 1
		}

		allowed_depth( nodes: readonly $shm_hitalama_dragtree_node[], insert: number, desired_depth: number ) {
			for( let depth = desired_depth; depth > 0; --depth ) {
				const parent = this.parent_by_depth( nodes, insert, depth )
				if( parent != null && this.can_have_children( parent ) ) return depth
			}
			return 0
		}

		parent_by_depth( nodes: readonly $shm_hitalama_dragtree_node[], insert: number, depth: number ) {
			if( depth === 0 ) return null
			for( let i = insert - 1; i >= 0; --i ) {
				if( nodes[ i ].depth === depth - 1 ) return nodes[ i ].id
			}
			return null
		}

		sibling_index( nodes: readonly $shm_hitalama_dragtree_node[], insert: number, parent: any ) {
			let index = 0
			for( let i = 0; i < insert; ++i ) {
				if( nodes[ i ].parent === parent ) ++index
			}
			return index
		}

		top_by_index( layout: $shm_hitalama_dragtree_layout, insert: number ) {
			return layout.tops[ insert ] ?? layout.height
		}

		node_y( id: any, nodes = this.visible_nodes() ) {
			const layout = this.layout( nodes )
			return layout.tops[ layout.index_by_id.get( id ) ?? 0 ] ?? 0
		}

		@ $mol_mem
		visible_nodes() {
			if( !this.drop_active() || this._drag_id == null ) return this.flat_nodes()
			const dragged = this._drag_id
			const base = this.base_nodes( dragged )
			const nodes = [ ...base ]
			nodes.splice( this.absolute_index( base, this.drop_parent(), this.drop_index() ), 0, {
				id: dragged,
				parent: this.drop_parent(),
				depth: this.drop_depth(),
			} )
			return nodes
		}

		absolute_index( nodes: readonly $shm_hitalama_dragtree_node[], parent: any, index: number ) {
			let sibling = 0
			const parent_node = parent == null ? null : nodes.find( node => node.id === parent )
			const parent_depth = parent_node?.depth ?? -1
			const start = parent_node ? nodes.indexOf( parent_node ) + 1 : 0

			for( let i = start; i < nodes.length; ++i ) {
				if( parent_node && nodes[ i ].depth <= parent_depth ) return i
				if( nodes[ i ].parent !== parent ) continue
				if( sibling === index ) return i
				++sibling
			}
			return nodes.length
		}

		@ $mol_mem
		flat_nodes() {
			const nodes = [] as $shm_hitalama_dragtree_node[]
			const walk = ( ids: readonly any[], parent: any, depth: number ) => {
				for( const id of ids ) {
					nodes.push( { id, parent, depth } )
					if( this.expanded( id ) ) walk( this.children( id ), id, depth + 1 )
				}
			}
			walk( this.roots(), null, 0 )
			return nodes
		}

		@ $mol_mem_key
		base_nodes( id: any ) {
			const subtree = this.subtree_ids( id )
			return this.flat_nodes().filter( node => !subtree.has( node.id ) )
		}

		@ $mol_mem_key
		subtree_ids( id: any ) {
			const nodes = this.flat_nodes()
			const index = this.layout( nodes ).index_by_id.get( id )
			const ids = new Set< any >()
			if( index == null ) return ids

			const depth = nodes[ index ].depth
			for( let i = index; i < nodes.length; ++i ) {
				const node = nodes[ i ]
				if( i > index && node.depth <= depth ) break
				ids.add( node.id )
			}
			return ids
		}

		layout( nodes: readonly $shm_hitalama_dragtree_node[] ): $shm_hitalama_dragtree_layout {
			const cached = this._layout_cache.get( nodes )
			if( cached ) return cached

			const height = this.item_height_default()
			const tops = [] as number[]
			const index_by_id = new Map< any, number >()
			const parent_by_id = new Map< any, any >()
			const depth_by_id = new Map< any, number >()

			for( let index = 0; index < nodes.length; ++index ) {
				const node = nodes[ index ]
				tops.push( index * height )
				index_by_id.set( node.id, index )
				parent_by_id.set( node.id, node.parent )
				depth_by_id.set( node.id, node.depth )
			}
			tops.push( nodes.length * height )

			const layout = {
				nodes,
				tops,
				index_by_id,
				parent_by_id,
				depth_by_id,
				height: nodes.length * height,
			}
			this._layout_cache.set( nodes, layout )
			return layout
		}

		is_same_or_child( id: any, parent: any ): boolean {
			return this.subtree_ids( parent ).has( id )
		}

		parent_of( id: any ) {
			return this.layout( this.flat_nodes() ).parent_by_id.get( id ) ?? null
		}

		ancestor_at_depth( id: any, depth: number ) {
			let current = id
			while( current != null && this.depth( current ) > depth ) {
				current = this.parent_of( current )
			}
			return this.depth( current ) === depth ? current : null
		}

		has_next_sibling( id: any ) {
			const parent = this.parent_of( id )
			const siblings = this.siblings( parent )
			const index = siblings.indexOf( id )
			return index >= 0 && index < siblings.length - 1
		}

		ancestor_at_depth_in_nodes( index: number, depth: number, nodes: readonly $shm_hitalama_dragtree_node[] ) {
			for( let i = index; i >= 0; --i ) {
				if( nodes[ i ].depth === depth ) return nodes[ i ].id
			}
			return null
		}

		has_next_sibling_in_nodes( id: any, nodes: readonly $shm_hitalama_dragtree_node[] ) {
			const index = nodes.findIndex( node => node.id === id )
			if( index < 0 ) return false

			const node = nodes[ index ]
			for( let i = index + 1; i < nodes.length; ++i ) {
				const next = nodes[ i ]
				if( next.depth < node.depth ) return false
				if( next.depth === node.depth && next.parent === node.parent ) return true
			}
			return false
		}

		find_parent( id: any, ids: readonly any[], parent: any ): any {
			for( const child of ids ) {
				if( child === id ) return parent
				const found = this.find_parent( id, this.children( child ), child )
				if( found !== undefined ) return found
			}
			return undefined
		}

		@ $mol_mem_key
		item_height( id: any ) {
			return this.item_height_default()
		}

		@ $mol_mem_key
		relative_y( id: any ) {
			return this.node_y( id )
		}

		@ $mol_mem_key
		relative_x( id: any ) {
			return this.depth( id ) * this.indent()
		}

		depth( id: any ) {
			return this.layout( this.visible_nodes() ).depth_by_id.get( id ) ?? 0
		}

		visible_index( id: any ) {
			return this.layout( this.visible_nodes() ).index_by_id.get( id ) ?? -1
		}

		item_padding_px( id: any ) {
			return '0px'
		}

		drop_display() {
			return this.drop_active() ? 'block' : 'none'
		}

		drop_top_px() {
			return this.drop_top() + 'px'
		}

		drop_left_px() {
			return ( this.drop_left() + this.drop_item_offset() ) + 'px'
		}

		drop_height_px() {
			return this.drop_height() + 'px'
		}

		drop_item_offset() {
			return this.drop_item_offset_default()
		}

		@ $mol_mem
		full_height_px() {
			return this.height() + 'px'
		}

		@ $mol_mem
		height() {
			return this.layout( this.visible_nodes() ).height
		}

	}
}
